import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { useState } from 'react';
import { Alert, Platform, ScrollView, Text, TextInput, View } from 'react-native';
import AppConstants from '../AppConstants.js';
import { useAPI } from '../contexts/APIContext.js';
import { useGeo } from '../contexts/GeoContext.js';
import { useJourney } from '../contexts/JourneyContext.js';
import SmallButton from '../elements/SmallButton.jsx';
import styles from '../Styles.jsx';

const helpData = [
  "Please make sure that you have the latest app version. Check your app store and make sure you're up to date.",
  "Your experience will be much better if you have GPS activated and have allowed this app to access your location information. Please check your settings.",
  "There is a tech support / phone recharge / hint station at MoreMusic at 13-17 Devonshire Road.",
  "You can also send us a message by tapping on the button below. It's already tied to your email and phone # so we will get back to you ASAP.",
  "Clues will get easier and more obvious over time, so just wait.",
  "Don't panic if a clue doesn't show up. You might need to wait a bit or get closer to your destination.",
  "Take breaks whenever you wish. This is not a race. Feel free to enjoy a snack or a beverage on the way.",
  "Note that the app must be open for new clues to arrive.",
  "When the time is right, the app will guide you to the secret location of the group finale."
];

export default function PanelHelp({ dismiss }) {
  const [isWritingHelpMessage, setIsWritingHelpMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  
  const { sendHelpMessage } = useAPI();

  const journey = useJourney();
  const geo = useGeo();

  const startHelpMessage = () => setIsWritingHelpMessage(true);

  const handleSendHelpMessage = async () => {
    try {
      await sendHelpMessage(journey.voyagerCode, geo.latitude, geo.longitude, journey.selectedLocation, messageText);
      setIsWritingHelpMessage(false);
      setMessageText('');
    } catch (error) {
      console.error('Error sending help message:', error);
    }
  };

  const handleUseClosedCaptions = () => {
    journey.setIsShowingClosedCaptioning(true);
    //if (useClosedCaptions) useClosedCaptions();
  };

  const handleDontUseClosedCaptions = () => {
    journey.setIsShowingClosedCaptioning(false);
    //if (dontUseClosedCaptions) dontUseClosedCaptions();
  };

  const handleUseLargerText = () => {
    journey.setIsShowingLargeText(true);
  };
  const handleUseRegularText = () => {
    journey.setIsShowingLargeText(false);
  };

  const handleResetAll = () => {
    const message = "This will sign you out and reset your app. Are you sure you want to continue?";

    if (Platform.OS === 'web') {
      // react-native's Alert is limited on web; use window.confirm for a working prompt
      if (window.confirm(message)) {
        journey.reset();
        dismiss();
      }
      return;
    }

    Alert.alert(
      'Reset Everything',
      message,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Reset Everything',
          style: 'destructive',
          onPress: () => {
            journey.reset();
            dismiss();
          }
        }
      ],
      { cancelable: true }
    );
  };

  const componentStyles = {  
    helpText: {
      textAlign: 'center',
      fontSize: 18,
      marginBottom: 18,
      padding: 5,
    },
    helpTextLarge: {
      textAlign: 'center',
      fontSize: 24,
      fontWeight: 'bold',
      marginBottom: 18,
      padding: 8,
    },
  };

  if (isWritingHelpMessage) {
    return (
      <View style={styles.locScreenFrame}>
        <ScrollView>
          <TextInput 
            editable={true}
            underlineColorAndroid='transparent'
            autoFocus={true}
            style={{
              ...styles.messageField,
              minHeight: 200,
              backgroundColor: '#f0f0f0',
              borderWidth: 1,
              borderColor: '#cccccc',
              borderRadius: 8,
              padding: 12,
              textAlignVertical: 'top'
            }}
            multiline={true}
            numberOfLines={4}
            value={messageText}
            onChangeText={setMessageText}
            placeholder="Type your message here..."
            placeholderTextColor="#999999"
          />
          <View style={{justifyContent: 'center', alignItems: 'center', gap: 10}}>
            <SmallButton
              onPress={handleSendHelpMessage}
              buttonStyle={styles.smallButton}
              title="Send Message"
            />
            <SmallButton
              onPress={() => {
                setIsWritingHelpMessage(false);
                setMessageText('');
              }}
              buttonStyle={styles.smallCloseButton}
              title="Cancel"
            />
          </View>
        </ScrollView>
      </View>
    );
  }

  const helpStyle = journey.isShowingLargeText ? componentStyles.helpTextLarge : componentStyles.helpText;

  return (
    <View style={styles.panelFrame}>
      <Text>Voyager: {journey.voyagerCode}</Text>
      <Text>{AppConstants.VERSION_ID} - {Application.nativeApplicationVersion || Constants.expoConfig?.version || 'N/A'} (b{Application.nativeBuildVersion || Constants.expoConfig?.ios?.buildNumber || Constants.expoConfig?.android?.versionCode || 'dev'})</Text>
      <ScrollView>
        {helpData.map((item, index) => (
          <View key={index}>
            <Text style={helpStyle}>{item}</Text>
          </View>
        ))}
      </ScrollView>
      
      <TextSizeButton
        isOn={journey.isShowingLargeText}
        turnOn={handleUseLargerText}
        turnOff={handleUseRegularText}
      />
      
      <CaptionsButton
        isOn={journey.isShowingClosedCaptioning}
        turnOn={handleUseClosedCaptions}
        turnOff={handleDontUseClosedCaptions}
      />
      
      <SmallButton
        onPress={startHelpMessage}
        buttonStyle={styles.smallButton}
        title="Send Help Message"
      />
      
      <SmallButton
        onPress={handleResetAll}
        title="Reset Everything"
      />

      <SmallButton
        onPress={dismiss}
        buttonStyle={styles.smallCloseButton}
        title="Done"
      />
    </View>
  );
}

// Helper components
function TextSizeButton({ isOn, turnOn, turnOff }) {
  return (
    <SmallButton
      onPress={isOn ? turnOff : turnOn}
      buttonStyle={styles.smallButton}
      title={isOn ? "Use Regular Text" : "Use Larger Text"}
    />
  );
}

function CaptionsButton({ isOn, turnOn, turnOff }) {
  return (
    <SmallButton
      onPress={isOn ? turnOff : turnOn}
      buttonStyle={styles.smallButton}
      title={isOn ? "Don't Show Captions" : "Show Captions"}
    />
  );
}
